import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
// import crashlytics from "@react-native-firebase/crashlytics";
// import messaging from "@react-native-firebase/messaging";
import { loginRequest } from '@app/api/token';
import { requestSsoLogout } from '@app/api/sso.api';
import { beginSessionEnd } from '@app/api/sessionState';
import { getConfig } from '@app/services/configService';
import { setSentryUser } from '@app/services/sentryService';
import { deleteToken, deleteUser } from '@app/services/localStorage.service';
import { isAdminToolSsoEnabled } from '@app/utils/sso';
import { ICanton } from '@app/interfaces/config';
import { ITokens } from '@app/interfaces/token';
import { ILoginBody } from '../../interfaces/login';
import { AxiosError } from 'axios';
import { homeFiltersActions } from '@app/store/slices/homeFilters';
import { inspectionsFiltersActions } from '@app/store/slices/inspectionsFilters';
import { breedingJournalFiltersActions } from '@app/store/slices/breedingJournalFilters';
import { observationsFiltersActions } from '@app/store/slices/observationsFilters';
import { IErrorData } from '@app/interfaces/apiError';
import { catchesFiltersActions } from './catchesFilters';

export interface IState extends ITokens {
  status: string;
  loading: boolean;
  wrongCredentials: boolean;
  isAuthenticated: boolean;
  checkedForSecureStoreTokens: boolean;
}

export const initialState = {
  accessToken: '',
  refreshToken: '',
  isAuthenticated: false,
  status: '',
  loading: false,
  wrongCredentials: false,
  checkedForSecureStoreTokens: false,
} as IState;

interface IErrorPayload {
  error_code: string;
}

// export const saveTokensInSecureStorage = async ({ accessToken, refreshToken }: ITokens) => {
//   await secureStoreSave('accessToken', accessToken);
//   await secureStoreSave('refreshToken', refreshToken);
// };

// const deleteTokensFromSecureStore = async () => {
//   await secureStoreDeleteByKey('accessToken');
//   await secureStoreDeleteByKey('refreshToken');
// };

const slice = createSlice({
  name: 'token',
  initialState,
  reducers: {
    setTokensFromSecureStore(state: IState, action: { payload: ITokens }) {
      const { accessToken, refreshToken } = action.payload;
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.isAuthenticated = true;
      state.checkedForSecureStoreTokens = true;
      state.wrongCredentials = initialState.wrongCredentials;
    },
    clearWrongCredentialsError(state: IState) {
      state.wrongCredentials = false;
    },
    setCheckedForSecureStoreTokens(state: IState, { payload }: { payload: boolean }) {
      state.checkedForSecureStoreTokens = payload;
    },
    setTokens(state: IState, action: { payload: ITokens }) {
      const { accessToken, refreshToken } = action.payload;
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      // Set isAuthenticated based on whether we have valid tokens
      state.isAuthenticated = !!(accessToken && refreshToken);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchToken.pending, (state: IState) => {
      state.status = 'loading';
      state.loading = true;
      state.wrongCredentials = false;
      state.isAuthenticated = false;
    });
    builder.addCase(
      fetchToken.fulfilled,
      (state: IState, { payload: { accessToken, refreshToken } }: { payload: ITokens }) => {
        state.accessToken = accessToken;
        state.refreshToken = refreshToken;
        state.loading = false;
        state.wrongCredentials = false;
        state.isAuthenticated = true;

        //   saveTokensInSecureStorage({ accessToken, refreshToken })
        //     .then(() => {})
        //     .catch(() => {});
      },
    );
    builder.addCase(fetchToken.rejected, (state: IState, { payload }) => {
      state.accessToken = initialState.accessToken;
      state.refreshToken = initialState.refreshToken;
      state.wrongCredentials = (payload as IErrorPayload).error_code === 'UnauthorizedCode';
      state.isAuthenticated = false;
      state.loading = false;
    });

    builder.addCase(logout.fulfilled, (state: IState) => {
      state.accessToken = initialState.accessToken;
      state.refreshToken = initialState.refreshToken;
      state.status = initialState.status;
      state.loading = initialState.loading;
      state.wrongCredentials = initialState.wrongCredentials;
      state.isAuthenticated = initialState.isAuthenticated;
    });

    builder.addCase(logout.rejected, (state: IState) => {
      state.accessToken = initialState.accessToken;
      state.refreshToken = initialState.refreshToken;
      state.status = initialState.status;
      state.loading = initialState.loading;
      state.wrongCredentials = initialState.wrongCredentials;
      state.isAuthenticated = initialState.isAuthenticated;
    });
  },
});

export const fetchToken = createAsyncThunk('token', async (body: ILoginBody, { rejectWithValue }) => {
  //   crashlytics().log('Fetch token / login request start.');
  try {
    const response = await loginRequest(body);
    // await messaging()
    //   .subscribeToTopic('LoggedInUsers')
    //   .then(() => console.log('Subscribed to LoggedInUserTopicName!'))
    //   .catch((e) => {
    //     console.log('error while subscribing to channel');
    //     console.log(e);
    //   });

    return response.data as ITokens;
  } catch (error) {
    const err = error as AxiosError<{ error_code: string }>;
    console.log(err);
    console.error(err);
    // crashlytics().log('Error while trying to fetch token / login.');
    // crashlytics().recordError(error as Error);
    const errorCode = err?.response?.data.error_code ? (err.response.data.error_code as string) : '';
    return rejectWithValue({ error_code: errorCode } as IErrorPayload);
  }
});

export const dispatchRefreshToken = createAsyncThunk('refreshToken', async (body: ILoginBody, { rejectWithValue }) => {
  //   crashlytics().log('Refresh token request start.');
  try {
    const response = await loginRequest(body);
    console.log(response);
    return response.data as ITokens;
  } catch (error) {
    const err = error as AxiosError;
    // crashlytics().log('Error while trying to request refresh token data.');
    // crashlytics().recordError(error as Error);
    const errorCode = (err.response?.data as IErrorData)?.error_code || '';
    return rejectWithValue({ error_code: errorCode } as IErrorPayload);
  }
});

interface LogoutOptions {
  skipSsoLogout?: boolean;
}

export const logout = createAsyncThunk('logout', async (options: LogoutOptions | undefined, { dispatch, getState }) => {
  // Silences the doomed requests a teardown produces.
  beginSessionEnd();

  const state = getState() as { token: IState; config: { currentCanton?: ICanton } };
  const accessToken = state.token.accessToken;

  // Always clear local tokens first so SSO/CORS failures cannot leave a session behind.
  dispatch(slice.actions.setTokens({ accessToken: '', refreshToken: '' }));
  deleteToken();
  deleteUser();
  setSentryUser(undefined);

  // Ensure redux-persist writes the empty tokens before navigation.
  const { persistor } = await import('@app/store/store');
  await persistor.flush();

  if (accessToken && !options?.skipSsoLogout && isAdminToolSsoEnabled(state.config.currentCanton)) {
    const { ssoLogoutPath } = getConfig();
    // Background best-effort; do not await or depend on the response.
    requestSsoLogout(ssoLogoutPath, accessToken);
  }

  dispatch(homeFiltersActions.resetFilters());
  dispatch(inspectionsFiltersActions.resetFilters());
  dispatch(catchesFiltersActions.resetFilters());
  dispatch(breedingJournalFiltersActions.resetFilters());
  dispatch(observationsFiltersActions.resetFilters());
});

export const { clearWrongCredentialsError, setTokensFromSecureStore, setCheckedForSecureStoreTokens, setTokens } =
  slice.actions;

export const token = slice.reducer;
